import React, { useState } from "react";

import { getWeightPullWeightClass } from "@eagerdog/constants";
import { IWeightPullWeighIn } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import Input from "src/components/Input/Input";
import Button from "src/components/Button/Button";
import { toast } from "src/components/Toast/ToastManager";

import styles from "./WeighInsModal.module.scss";

interface IProps {
  eventId: string;
  dog: IWeightPullWeighIn;
  onSave(updated: IWeightPullWeighIn[]): void;
  onCancel(): void;
}

// Small "Enter Weight" modal — records a dog's weight once for the whole
// event. The weight class is auto-computed (never manually chosen) and
// previewed live. Saving applies to every Weight Pull trial this dog is
// entered in at the event (handled server-side).
const EnterWeightModal: React.FC<IProps> = (props) => {
  const [weight, setWeight] = useState<string>(
    props.dog.dog_weight ? props.dog.dog_weight.toString() : ""
  );
  const [weighedBy, setWeighedBy] = useState<string>(props.dog.weighed_by || "");
  const [saving, setSaving] = useState<boolean>(false);

  const weightNum = Number(weight);
  const isValid = weight.length > 0 && weightNum > 0;
  const previewClass = isValid ? getWeightPullWeightClass(weightNum) : undefined;

  const handleSave = () => {
    if (!isValid) {
      toast.show({
        title: "Enter Weight",
        content: "Please enter a valid weight",
        duration: 8000,
        type: "fail",
      });
      return;
    }

    setSaving(true);
    apiService
      .updateDogWeighIn(props.eventId, props.dog.dog_id, weightNum, weighedBy)
      .then((updated) => {
        props.onSave(updated);
      })
      .catch((error: any) => {
        toast.show({
          title: "Enter Weight",
          content: error?.response?.data?.message || "Unable to save this dog's weight",
          duration: 10000,
          errorDetails: error,
          type: "fail",
        });
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className={styles.enterWeightModalContent}>
      <h2>Enter Weight</h2>
      <div className={styles.dogName}>{props.dog.call_name}</div>
      <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <Input
          type="number"
          min="0"
          step="0.1"
          autoFocus
          label="Dog Weight (lbs)"
          value={weight}
          onChange={(e) => { setWeight(e.target.value); }}
        />
        <div className={styles.weighedByInput}>
          <Input
            type="text"
            label="Weighed By Initials"
            value={weighedBy}
            onChange={(e) => { setWeighedBy(e.target.value); }}
          />
        </div>
        {previewClass && (
          <div className={styles.classPreview}>
            Weight Class: <strong>{previewClass}</strong>
          </div>
        )}
        <div className={styles.actions}>
          <Button type="button" variant="secondary" onClick={props.onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={saving} disabled={!isValid}>
            Save
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EnterWeightModal;
